import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
    faBuilding,
    faLandmark,
    faLayerGroup,
    faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"

export enum BulkRunOutcome {
    FULL_COVERAGE = "FULL_COVERAGE",
    COST_DETAILS_UNAVAILABLE = "COST_DETAILS_UNAVAILABLE",
    POLICY_INACTIVE = "POLICY_INACTIVE",
    MEMBER_NOT_FOUND = "MEMBER_NOT_FOUND",
    PAYER_UNAVAILABLE = "PAYER_UNAVAILABLE",
    CREDENTIALING_ISSUE = "CREDENTIALING_ISSUE",
    ENROLLMENT_INCOMPLETE = "ENROLLMENT_INCOMPLETE",
}

export enum BulkResponsibilityBucket {
    IN_DEDUCTIBLE = "IN_DEDUCTIBLE",
    COPAY = "COPAY",
    COINSURANCE = "COINSURANCE",
    FULLY_COVERED = "FULLY_COVERED",
}

export enum BulkPlanFinding {
    TPA = "TPA",
    ADDITIONAL_POLICY = "ADDITIONAL_POLICY",
    MEDICAID = "MEDICAID",
    MEDICARE = "MEDICARE",
    OUT_OF_NETWORK = "OUT_OF_NETWORK",
}

export const BULK_RESULTS_INSIGHTS_CONFIG = {
    resultsPageSize: 10,

    modelUpdateIntervalMs: 5 * 1000,

    outcomeOrder: [
        BulkRunOutcome.FULL_COVERAGE,
        BulkRunOutcome.COST_DETAILS_UNAVAILABLE,
        BulkRunOutcome.POLICY_INACTIVE,
        BulkRunOutcome.MEMBER_NOT_FOUND,
        BulkRunOutcome.PAYER_UNAVAILABLE,
        BulkRunOutcome.CREDENTIALING_ISSUE,
        BulkRunOutcome.ENROLLMENT_INCOMPLETE,
    ] satisfies BulkRunOutcome[],

    outcomeLabels: {
        [BulkRunOutcome.FULL_COVERAGE]: "Full coverage picture",
        [BulkRunOutcome.COST_DETAILS_UNAVAILABLE]:
            "Plan found, cost details unavailable",
        [BulkRunOutcome.POLICY_INACTIVE]: "Policy inactive",
        [BulkRunOutcome.MEMBER_NOT_FOUND]: "Member not found",
        [BulkRunOutcome.PAYER_UNAVAILABLE]: "Payer unavailable",
        [BulkRunOutcome.CREDENTIALING_ISSUE]: "Credentialing issue",
        [BulkRunOutcome.ENROLLMENT_INCOMPLETE]: "Enrollment incomplete",
    } satisfies Record<BulkRunOutcome, string>,

    outcomeColors: {
        [BulkRunOutcome.FULL_COVERAGE]: NIRVANA_COLORS.brand.stormyPurple,
        [BulkRunOutcome.COST_DETAILS_UNAVAILABLE]: NIRVANA_COLORS.brand.lilac,
        [BulkRunOutcome.POLICY_INACTIVE]: NIRVANA_COLORS.brand.warmShadow,
        [BulkRunOutcome.MEMBER_NOT_FOUND]: NIRVANA_COLORS.brand.warningYellow,
        [BulkRunOutcome.PAYER_UNAVAILABLE]: NIRVANA_COLORS.brand.orange,
        [BulkRunOutcome.CREDENTIALING_ISSUE]: NIRVANA_COLORS.brand.error2,
        [BulkRunOutcome.ENROLLMENT_INCOMPLETE]: NIRVANA_COLORS.brand.redDark,
    } satisfies Record<BulkRunOutcome, string>,

    outcomeActions: {
        [BulkRunOutcome.FULL_COVERAGE]: "No action needed.",
        [BulkRunOutcome.COST_DETAILS_UNAVAILABLE]: "Retry these checks.",
        [BulkRunOutcome.POLICY_INACTIVE]: "Run SmartScan.",
        [BulkRunOutcome.MEMBER_NOT_FOUND]: "Review demographics.",
        [BulkRunOutcome.PAYER_UNAVAILABLE]: "Retry checks.",
        [BulkRunOutcome.CREDENTIALING_ISSUE]: "View affected payers.",
        [BulkRunOutcome.ENROLLMENT_INCOMPLETE]: "View affected payers.",
    } satisfies Record<BulkRunOutcome, string>,

    outcomeActionIsLink: {
        [BulkRunOutcome.FULL_COVERAGE]: false,
        [BulkRunOutcome.COST_DETAILS_UNAVAILABLE]: true,
        [BulkRunOutcome.POLICY_INACTIVE]: true,
        [BulkRunOutcome.MEMBER_NOT_FOUND]: true,
        [BulkRunOutcome.PAYER_UNAVAILABLE]: true,
        [BulkRunOutcome.CREDENTIALING_ISSUE]: true,
        [BulkRunOutcome.ENROLLMENT_INCOMPLETE]: true,
    } satisfies Record<BulkRunOutcome, boolean>,

    responsibilityOrder: [
        BulkResponsibilityBucket.IN_DEDUCTIBLE,
        BulkResponsibilityBucket.COPAY,
        BulkResponsibilityBucket.COINSURANCE,
        BulkResponsibilityBucket.FULLY_COVERED,
    ] satisfies BulkResponsibilityBucket[],

    responsibilityLabels: {
        [BulkResponsibilityBucket.IN_DEDUCTIBLE]: "In deductible phase",
        [BulkResponsibilityBucket.COPAY]: "Copay plans",
        [BulkResponsibilityBucket.COINSURANCE]: "Coinsurance plans",
        [BulkResponsibilityBucket.FULLY_COVERED]: "Fully covered",
    } satisfies Record<BulkResponsibilityBucket, string>,

    responsibilityDescriptions: {
        [BulkResponsibilityBucket.IN_DEDUCTIBLE]:
            "These patients owe the full session rate until the deductible is met — collect at intake.",
        [BulkResponsibilityBucket.COPAY]:
            "These patients owe a copay at each session — collect at intake.",
        [BulkResponsibilityBucket.COINSURANCE]:
            "These patients owe a percentage of the session rate until the out-of-pocket maximum is met — collect at intake.",
        [BulkResponsibilityBucket.FULLY_COVERED]:
            "These patients are fully covered by their insurance plan — no collection needed.",
    } satisfies Record<BulkResponsibilityBucket, string>,

    findingOrder: [
        BulkPlanFinding.TPA,
        BulkPlanFinding.ADDITIONAL_POLICY,
        BulkPlanFinding.MEDICAID,
        BulkPlanFinding.MEDICARE,
        BulkPlanFinding.OUT_OF_NETWORK,
    ] satisfies BulkPlanFinding[],

    findingLabels: {
        [BulkPlanFinding.TPA]: "Third-party administrators",
        [BulkPlanFinding.ADDITIONAL_POLICY]: "Additional policies detected",
        [BulkPlanFinding.MEDICAID]: "Medicaid",
        [BulkPlanFinding.MEDICARE]: "Medicare",
        [BulkPlanFinding.OUT_OF_NETWORK]: "Out of network",
    } satisfies Record<BulkPlanFinding, string>,

    findingTagLabels: {
        [BulkPlanFinding.TPA]: "TPA",
        [BulkPlanFinding.ADDITIONAL_POLICY]: "ADD",
        [BulkPlanFinding.MEDICAID]: "MCD",
        [BulkPlanFinding.MEDICARE]: "MCR",
        [BulkPlanFinding.OUT_OF_NETWORK]: "OON",
    } satisfies Record<BulkPlanFinding, string>,

    findingIcons: {
        [BulkPlanFinding.TPA]: faBuilding,
        [BulkPlanFinding.ADDITIONAL_POLICY]: faLayerGroup,
        [BulkPlanFinding.MEDICAID]: faLandmark,
        [BulkPlanFinding.MEDICARE]: faLandmark,
        [BulkPlanFinding.OUT_OF_NETWORK]: faTriangleExclamation,
    } satisfies Record<BulkPlanFinding, IconDefinition>,

    additionalPolicySubtypeLabels: {
        commercial: "Secondary commercial",
        medicare: "Medicare secondary",
        unidentified: "Unidentified payer",
    } satisfies Record<"commercial" | "medicare" | "unidentified", string>,

    medicaidSubtypeLabels: {
        managed: "Managed Medicaid",
        ffs: "Fee-for-service",
    } satisfies Record<"managed" | "ffs", string>,

    medicareSubtypeLabels: {
        advantage: "Advantage",
        supplemental: "Supplemental",
        msp: "MSP",
        original: "Original",
    } satisfies Record<
        "advantage" | "supplemental" | "msp" | "original",
        string
    >,

    oonSubtypeLabels: {
        notContracted: "Payer not contracted",
        excludedByRule: "Plan excluded by rule",
    } satisfies Record<"notContracted" | "excludedByRule", string>,
}
